const AUTHENTIK_URL = process.env.REACT_APP_AUTHENTIK_URL || 'http://localhost:9000';
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || 'nuvia';
const REDIRECT_URI = `${window.location.origin}/callback`;

// Generate random string for PKCE
function generateRandomString(length) {
  const possible = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let text = '';
  for (let i = 0; i < length; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text;
}

// SHA256 hash for PKCE challenge
async function sha256(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await window.crypto.subtle.digest('SHA-256', data);
  return hash;
}

// Base64 URL encode
function base64urlencode(buffer) {
  let str = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// Generate PKCE challenge
async function generateCodeChallenge(codeVerifier) {
  const hashed = await sha256(codeVerifier);
  return base64urlencode(hashed);
}

export const auth = {
  /**
   * Initiate OAuth2 login flow with Authentik
   */
  login: async () => {
    // Generate PKCE parameters
    const codeVerifier = generateRandomString(128);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);

    // Store for later verification
    sessionStorage.setItem('pkce_code_verifier', codeVerifier);
    sessionStorage.setItem('oauth_state', state);

    // Build authorization URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      scope: 'openid profile email groups',
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    const authUrl = `${AUTHENTIK_URL}/application/o/authorize/?${params.toString()}`;

    // Redirect to Authentik
    window.location.href = authUrl;
  },

  /**
   * Check if user is authenticated
   * Returns true only if token exists and appears valid
   */
  isAuthenticated: () => {
    const token = localStorage.getItem('access_token');
    if (!token) return false;

    try {
      // Parse JWT token to check expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp * 1000; // Convert to milliseconds

      // Check if token is expired (with 10 second buffer)
      if (Date.now() >= exp - 10000) {
        // Token is expired, clean up
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        return false;
      }

      return true;
    } catch (e) {
      // Invalid token format, clean up
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      return false;
    }
  },

  /**
   * Get stored access token
   */
  getToken: () => {
    return localStorage.getItem('access_token');
  },

  /**
   * Logout user - clears all auth data
   */
  logout: () => {
    // Clear all authentication data
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('pkce_code_verifier');
    sessionStorage.removeItem('oauth_state');

    // Note: We don't clear theme preference (nuvia-theme)
    // as that's a user preference independent of auth state

    // Redirect to login page
    window.location.href = '/login';
  },
};

export default auth;
